import type { MultiPolygon, Polygon, Position } from 'geojson';
import type { DeliveryMapBounds } from './deliveryMapTypes';

const LONGITUDE_SPAN = 360;
type HorizontalViewport = Pick<DeliveryMapBounds, 'west' | 'east'>;

function visiblePolygonCopies(coordinates: Position[][], viewport: HorizontalViewport): Position[][][] {
  const exterior = coordinates[0];
  if (!exterior?.length) return [];
  let west = Infinity;
  let east = -Infinity;
  for (const position of exterior) {
    if (!Number.isFinite(position[0])) return [];
    west = Math.min(west, position[0]);
    east = Math.max(east, position[0]);
  }
  const firstCopy = Math.ceil((viewport.west - east) / LONGITUDE_SPAN);
  const lastCopy = Math.floor((viewport.east - west) / LONGITUDE_SPAN);
  if (!Number.isSafeInteger(firstCopy) || !Number.isSafeInteger(lastCopy)) return [];
  const copies: Position[][][] = [];
  for (let copy = firstCopy; copy <= lastCopy; copy += 1) {
    const longitudeOffset = LONGITUDE_SPAN * copy;
    // Translate whole polygons and their holes; never unwrap native edges.
    const translated = coordinates.map((ring) => ring.map(([longitude, ...ordinates]) => [longitude + longitudeOffset, ...ordinates]));
    copies.push(translated);
  }
  return copies;
}

export function positionDeliveryMapGeometry(geometry: Polygon | MultiPolygon, viewport: HorizontalViewport): MultiPolygon {
  const coordinates: Position[][][] = [];
  const isValidViewport = Number.isFinite(viewport.west) && Number.isFinite(viewport.east) && viewport.west <= viewport.east;
  if (!isValidViewport) return { type: 'MultiPolygon', coordinates };
  const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
  for (const polygon of polygons) {
    const copies = visiblePolygonCopies(polygon, viewport);
    coordinates.push(...copies);
  }
  return { type: 'MultiPolygon', coordinates };
}
